import { yupResolver } from '@hookform/resolvers/yup';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { TREAD_UUID, useCompany } from '~hooks/useCompany/useCompany';
import { PaymentTerm } from '~hooks/usePaymentTerms/models';
import usePaymentTerms from '~hooks/usePaymentTerms/usePaymentTerms';
import { useStores } from '~store';
import UserStore from '~store/UserStore';
import { alert, AlertTypes } from '~types/AlertTypes';

// The empty id that stands for "no default term"; not a real payment term id
const NO_DEFAULT = '';

interface PaymentTermOption {
  id: string;
  label: string;
}

const settingSchema = yup.object().shape({
  paymentTerm: yup
    .object({
      id: yup.string().defined(),
      label: yup.string().defined(),
    })
    .required(),
});

type SettingDto = yup.InferType<typeof settingSchema>;

function toOption(term: PaymentTerm): PaymentTermOption {
  return { id: term.id, label: `${term.name} (${term.dueDays})` };
}

function DefaultPaymentTermSetting() {
  const { userStore, toasterStore } = useStores();
  const { updateCompany } = useCompany();
  const { fetchAll } = usePaymentTerms();

  const [paymentTerms, setPaymentTerms] = useState<PaymentTerm[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [inSaveMode, setInSaveMode] = useState(false);

  const persistedTermId = userStore.userCompany.defaultPaymentTermId ?? NO_DEFAULT;
  const noDefaultOption: PaymentTermOption = { id: NO_DEFAULT, label: t('common.none') };
  const options = [
    noDefaultOption,
    ...paymentTerms.filter((term) => term.active).map(toOption),
  ];
  const persistedOption =
    options.find((option) => option.id === persistedTermId) ?? noDefaultOption;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SettingDto>({
    resolver: yupResolver(settingSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: { paymentTerm: persistedOption },
  });

  useEffect(() => {
    if (!userStore.userCompany.id) {
      return;
    }

    setIsFetching(true);
    fetchAll(userStore.userCompany.id)
      .then(setPaymentTerms)
      .finally(() => setIsFetching(false));
  }, [fetchAll, userStore.userCompany.id]);

  // The form is constructed before the picker's options finish loading, so its initial
  // default value can't resolve to the persisted term yet — reseed once the fetch
  // lands. Skipped while editing so a landing response can't clobber an in-progress pick.
  useEffect(() => {
    if (inSaveMode) {
      return;
    }

    reset({ paymentTerm: persistedOption });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentTerms, persistedTermId, inSaveMode]);

  const saveCompanyDefaultPaymentTerm = (data: SettingDto) => {
    setIsSaving(true);
    updateCompany({
      company: {
        id: userStore.userCompany.id,
        defaultPaymentTermId: data.paymentTerm.id || null,
      },
    })
      .then(() => {
        setInSaveMode(false);
        reset({ paymentTerm: data.paymentTerm });
        toasterStore.push(
          alert(
            t('administration.configuration.configuration_saved'),
            AlertTypes.success,
          ),
        );
      })
      // updateCompany routes failures through connection.handleRequestError, which
      // toasts + re-throws; swallow so we don't double-toast or leak a rejection.
      .catch(() => undefined)
      .finally(() => {
        setIsSaving(false);
      });
  };

  return (
    <Grid container spacing={2}>
      <Grid size={{ sm: 3 }}>
        <AutocompleteFormField
          control={control}
          disabled={!inSaveMode}
          errors={errors}
          getLabel={(option?: PaymentTermOption) => option?.label ?? ''}
          getValue={(option?: PaymentTermOption) => option?.id ?? ''}
          label={t(
            'administration.configuration.settings.default_payment_term.setting_title',
          )}
          list={options}
          loading={isFetching}
          name="paymentTerm"
        />
      </Grid>
      <Grid sx={{ alignSelf: 'flex-end', mb: 1 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {inSaveMode && (
            <SmallButton
              color="secondary"
              variant="outlined"
              disabled={isSaving}
              onClick={() => {
                setInSaveMode(false);
                reset({ paymentTerm: persistedOption });
              }}
              startIcon={<Close />}
            >
              {t('actions.cancel')}
            </SmallButton>
          )}
          <SmallLoadingButton
            disabled={!inSaveMode && isFetching}
            loading={inSaveMode ? isSaving : isFetching}
            variant="contained"
            color="brandV2Yellow"
            startIcon={inSaveMode ? <Check /> : null}
            onClick={
              inSaveMode
                ? handleSubmit(saveCompanyDefaultPaymentTerm)
                : () => setInSaveMode(true)
            }
          >
            {inSaveMode ? t('actions.save') : t('actions.edit')}
          </SmallLoadingButton>
        </Box>
      </Grid>
    </Grid>
  );
}

export default {
  component: DefaultPaymentTermSetting,
  canAccess: (userStore: UserStore) => {
    if (!userStore.getPermissions().canEditCompany) return false;
    // The Tread super-company cannot be updated via the standard endpoint; hide the
    // setting entirely for platform admins logged into Tread to avoid a dead-end UI.
    return userStore.userCompany.id !== TREAD_UUID;
  },
};
